import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";

/**
 * Tracks whether the current session has been flagged as expired by the
 * auth interceptor. Surfaced via the inline banner in the admin layout
 * so the user can re-login WITHOUT losing in-progress form state.
 */
@Injectable({ providedIn: "root" })
export class SessionExpiryService {
  private static readonly INTENDED_URL_KEY = "session.intendedUrl";

  private readonly _expired$ = new BehaviorSubject<boolean>(false);
  readonly expired$ = this._expired$.asObservable();

  /**
   * Flag the session as expired. Called by the auth interceptor when a
   * 401/403 + refresh-failure cascade happens. Stores the URL the user
   * was on so we can restore it after re-login.
   */
  markExpired(intendedUrl?: string | null): void {
    if (intendedUrl) {
      try {
        localStorage.setItem(
          SessionExpiryService.INTENDED_URL_KEY,
          intendedUrl,
        );
      } catch {
        /* localStorage may be full / disabled - ignore. */
      }
    }
    this._expired$.next(true);
  }

  /**
   * Clear the expired flag (called once the user successfully re-logins).
   */
  reset(): void {
    this._expired$.next(false);
  }

  /**
   * Pop the saved intended URL (one-shot).
   */
  consumeIntendedUrl(): string | null {
    try {
      const url = localStorage.getItem(SessionExpiryService.INTENDED_URL_KEY);
      localStorage.removeItem(SessionExpiryService.INTENDED_URL_KEY);
      return url;
    } catch {
      return null;
    }
  }

  isExpired(): boolean {
    return this._expired$.value;
  }
}
